import { useEffect, useState } from "react";
import { Printer, X, FileText, Loader2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useBranding } from "@/lib/branding";
import { endpoints } from "@/lib/api";

// DocViewer — renders the demo artifacts (Ariba PR, Dell PO, WMS manifest,
// SN Provisioning Task, Blancco wipe cert, ITAD grading report) as printable
// documents. Print button uses window.print() with a scoped print stylesheet.

export function DocViewer({ open, onOpenChange, docIds = [], initialDocId = null }) {
  const { brand } = useBranding();
  const [meta, setMeta] = useState({});
  const [docs, setDocs] = useState({});
  const [active, setActive] = useState(initialDocId || docIds[0]);
  const [loading, setLoading] = useState(false);
  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";

  useEffect(() => {
    if (!open) return;
    endpoints.lifecycleDocsList().then((r) => setMeta(r.doc_meta || {})).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !active || docs[active]) return;
    setLoading(true);
    endpoints.lifecycleDoc(active)
      .then((d) => setDocs((prev) => ({ ...prev, [active]: d })))
      .finally(() => setLoading(false));
  }, [open, active, docs]);

  useEffect(() => {
    if (open && initialDocId) setActive(initialDocId);
  }, [open, initialDocId]);

  const activeDoc = docs[active];
  const activeMeta = meta[active];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] p-0 overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 print:hidden">
          <div className="flex items-center gap-2 overflow-x-auto">
            {docIds.map((id) => {
              const m = meta[id];
              const isActive = id === active;
              return (
                <button
                  key={id}
                  data-testid={`doc-tab-${id}`}
                  onClick={() => setActive(id)}
                  className="text-xs font-semibold h-8 px-3 rounded-md flex items-center gap-1.5 whitespace-nowrap transition-colors"
                  style={isActive
                    ? { backgroundColor: brandBlue, color: "#fff" }
                    : { color: "#64748B", border: `1px solid ${brandSoft}` }}
                >
                  <span
                    className="w-4 h-4 rounded grid place-items-center text-[8px] font-bold"
                    style={{ backgroundColor: isActive ? "#ffffff33" : brandSoft, color: isActive ? "#fff" : brandBlue }}
                  >
                    {m?.monogram || "·"}
                  </span>
                  {m?.title || id}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1">
            <Button
              data-testid="doc-print-btn"
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              className="h-8"
            >
              <Printer size={12} className="mr-1.5" />
              Print / PDF
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="h-8 w-8 p-0">
              <X size={14} />
            </Button>
          </div>
        </div>

        {/* Doc body */}
        <div className="overflow-y-auto max-h-[calc(92vh-60px)] bg-slate-100 print:bg-white p-6 print:p-0">
          {loading ? (
            <div className="py-16 text-center text-slate-500">
              <Loader2 size={20} className="mx-auto mb-3 animate-spin" style={{ color: brandBlue }} />
              Generating {activeMeta?.title || "document"}…
            </div>
          ) : activeDoc ? (
            <DocPaper doc={activeDoc} meta={activeMeta} brandBlue={brandBlue} brandSoft={brandSoft} />
          ) : null}
        </div>
      </DialogContent>

      {/* Print stylesheet */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          [data-testid="doc-paper"], [data-testid="doc-paper"] * { visibility: visible !important; }
          [data-testid="doc-paper"] { position: absolute; top: 0; left: 0; width: 100%; }
        }
      `}</style>
    </Dialog>
  );
}

// ==================== Doc paper (renders any of the 6 doc types) ====================
function DocPaper({ doc, meta, brandBlue, brandSoft }) {
  return (
    <div className="max-w-[820px] mx-auto bg-white rounded-md shadow-sm border border-slate-200 p-8 print:shadow-none print:border-0" data-testid="doc-paper">
      {/* Letterhead */}
      <div className="flex items-start justify-between border-b border-slate-300 pb-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg grid place-items-center text-white font-bold text-sm" style={{ backgroundColor: brandBlue }}>
            {meta?.monogram || doc.system?.[0]}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{doc.system}</div>
            <div className="text-lg font-bold text-slate-900">{doc.title}</div>
          </div>
        </div>
        <div className="text-right text-xs">
          <div className="font-mono font-bold text-slate-900">{doc.number}</div>
          <div className="text-slate-500 mt-1">{doc.date}</div>
          <div className="text-[10px] uppercase tracking-widest font-semibold mt-1 px-2 py-0.5 rounded inline-block"
            style={{ backgroundColor: brandSoft, color: brandBlue }}>
            {doc.status}
          </div>
        </div>
      </div>

      {/* Body sections — render the interesting parts of each doc type */}
      <div className="space-y-5 text-[13px] text-slate-800">
        {doc.requester && <TwoCol left={{ h: "Requester", body: doc.requester }} right={{ h: "Ship to", body: doc.ship_to }} />}
        {doc.legal_entity && <KV label="Legal entity" value={doc.legal_entity} />}
        {doc.delivery_by && <KV label="Delivery by" value={doc.delivery_by} />}
        {doc.buyer && <TwoCol left={{ h: "Buyer", body: doc.buyer }} right={{ h: "Seller", body: doc.seller }} />}
        {doc.parent_request && <KV label="Parent request" value={doc.parent_request} mono />}
        {doc.assigned_to && <KV label="Assigned to" value={doc.assigned_to} />}
        {doc.from && <TwoCol left={{ h: "From", body: { addr: doc.from } }} right={{ h: "To", body: { addr: doc.to } }} />}
        {doc.carrier && <KV label="Carrier · ETA" value={`${doc.carrier} · ${doc.eta}`} />}
        {doc.standard && <KV label="Standard" value={doc.standard} />}
        {doc.device && <TableSection title="Device" rows={Object.entries(doc.device).map(([k, v]) => [prettifyKey(k), String(v)])} />}
        {doc.erasure && <TableSection title="Erasure" rows={Object.entries(doc.erasure).map(([k, v]) => [prettifyKey(k), String(v)])} />}
        {doc.operator && <KV label="Certified operator" value={`${doc.operator.name} · ${doc.operator.id}`} />}
        {doc.hash_sha256 && <KV label="Evidence hash (SHA-256)" value={doc.hash_sha256} mono />}
        {/* Wet-ink signature + QR for defensible printouts (Blancco / ITAD) */}
        {(doc.operator || doc.decision) && (doc.id === "wipe_certificate" || doc.id === "itad_grading") && (
          <SignaturePanel doc={doc} brandBlue={brandBlue} brandSoft={brandSoft} />
        )}
        {doc.inspection && (
          <TableSection title="Inspection" rows={doc.inspection.map((r) => [r.item, r.result])} />
        )}
        {doc.market && <TableSection title="Market" rows={Object.entries(doc.market).map(([k, v]) => [prettifyKey(k), typeof v === "number" ? `$${v.toLocaleString()}` : String(v)])} />}
        {doc.decision && (
          <div className="rounded-lg p-4" style={{ backgroundColor: brandSoft }}>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: brandBlue }}>Disposition decision</div>
            <div className="text-sm font-semibold text-slate-900">{doc.decision.action} — {doc.decision.reason}</div>
            {doc.decision.assigned_to_cohort && (
              <div className="text-[11px] text-slate-600 mt-1">Assigned to: <span className="font-semibold">{doc.decision.assigned_to_cohort}</span></div>
            )}
          </div>
        )}

        {/* Line items — Ariba/Dell */}
        {doc.line_items && (
          <div>
            <SectionH>Line items</SectionH>
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-2 px-2 w-8">#</th>
                  <th className="text-left py-2 px-2">Description</th>
                  <th className="text-right py-2 px-2 w-16">Qty</th>
                  <th className="text-right py-2 px-2 w-24">Unit</th>
                  <th className="text-right py-2 px-2 w-28">Extended</th>
                </tr>
              </thead>
              <tbody>
                {doc.line_items.map((li) => (
                  <tr key={li.line} className="border-t border-slate-100">
                    <td className="py-2 px-2 font-mono text-slate-500">{li.line}</td>
                    <td className="py-2 px-2">{li.description || li.sku}</td>
                    <td className="py-2 px-2 text-right font-semibold">{li.qty}</td>
                    <td className="py-2 px-2 text-right">€{li.unit_price.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right font-semibold">€{(li.qty * li.unit_price).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Totals */}
        {doc.totals && (
          <div className="rounded-lg border border-slate-200 p-4">
            <SectionH className="mb-3">Totals ({doc.totals.currency || ""})</SectionH>
            <div className="space-y-1.5 text-sm">
              {Object.entries(doc.totals).filter(([k]) => k !== "currency").map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-slate-500">{prettifyKey(k)}</span>
                  <span className={k === "grand_total" || k === "total_minutes" ? "font-bold text-slate-900" : "text-slate-800"}>
                    {typeof v === "number" ? (k.includes("pct") ? `${v}%` : (k.includes("minutes") ? `${v} min` : `€${v.toLocaleString(undefined, {minimumFractionDigits: 2})}`)) : String(v)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* At-risk breakdown (shipment) */}
        {doc.at_risk_breakdown && (
          <div>
            <SectionH>At-risk breakdown</SectionH>
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left py-2 px-2">Reason</th><th className="text-right py-2 px-2 w-16">Count</th><th className="text-left py-2 px-2">Remediation</th></tr>
              </thead>
              <tbody>
                {doc.at_risk_breakdown.map((r) => (
                  <tr key={r.reason} className="border-t border-slate-100">
                    <td className="py-2 px-2 font-semibold">{r.reason}</td>
                    <td className="py-2 px-2 text-right font-bold">{r.count}</td>
                    <td className="py-2 px-2 text-slate-600">{r.remediation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Sample shipment rows */}
        {doc.sample_rows && (
          <div>
            <SectionH>Sample tracking · {doc.summary?.total_devices} recipients</SectionH>
            <table className="w-full text-[11px] font-mono">
              <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left py-2 px-2">Tracking</th><th className="text-left py-2 px-2">Device</th><th className="text-left py-2 px-2">Recipient</th><th className="text-left py-2 px-2">Address</th></tr>
              </thead>
              <tbody>
                {doc.sample_rows.map((r) => (
                  <tr key={r.tracking} className="border-t border-slate-100">
                    <td className="py-2 px-2">{r.tracking}</td><td className="py-2 px-2">{r.device}</td><td className="py-2 px-2 font-sans font-semibold">{r.recipient}</td><td className="py-2 px-2 font-sans text-slate-600">{r.address}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Task list (SN Provisioning) */}
        {doc.task_list && (
          <div>
            <SectionH>Task steps</SectionH>
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {doc.task_list.map((t) => (
                <div key={t.step} className="flex items-center justify-between px-3 py-2 text-[12px]">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full grid place-items-center text-white text-[9px] font-bold" style={{ backgroundColor: "#10B981" }}>✓</div>
                    <span className="font-semibold text-slate-900">{t.step}</span>
                  </div>
                  <span className="text-slate-500">{t.duration_min} min</span>
                </div>
              ))}
            </div>
            {doc.notes && <div className="text-[11px] text-slate-500 mt-2 italic">{doc.notes}</div>}
          </div>
        )}

        {/* Chain of custody (Blancco) */}
        {doc.chain_of_custody && (
          <div>
            <SectionH>Chain of custody</SectionH>
            <div className="space-y-1.5">
              {doc.chain_of_custody.map((e, i) => (
                <div key={i} className="flex gap-3 text-[12px] py-1 border-b border-slate-100 last:border-0">
                  <span className="font-mono text-slate-500 shrink-0 w-40">{e.at}</span>
                  <span className="text-slate-800">{e.event}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approvals (Ariba) */}
        {doc.approvals && (
          <div>
            <SectionH>Approvals</SectionH>
            <div className="space-y-1.5">
              {doc.approvals.map((a, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-[12px]">
                  <div><span className="text-slate-500">{a.role}: </span><span className="font-semibold">{a.name}</span></div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="px-2 py-0.5 rounded font-semibold" style={{ backgroundColor: "#ECFDF5", color: "#0F766E" }}>{a.decision}</span>
                    <span className="text-slate-400 font-mono">{a.at}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pt-4 mt-4 border-t border-slate-200 text-[10px] text-slate-400 flex items-center justify-between">
          <span>Generated by Flexspace Lifecycle Intelligence · {new Date().toISOString().slice(0, 10)}</span>
          {doc.linked?.ariba_url && (
            <a href={doc.linked.ariba_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:underline" style={{ color: brandBlue }}>
              Open in {doc.system} <ExternalLink size={9} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionH({ children, className = "" }) {
  return <div className={`text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 ${className}`}>{children}</div>;
}

function KV({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3 text-[12px]">
      <span className="text-slate-500 min-w-[140px]">{label}:</span>
      <span className={`text-slate-900 font-semibold ${mono ? "font-mono text-[11px]" : ""}`}>{value}</span>
    </div>
  );
}

function TwoCol({ left, right }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[left, right].filter(Boolean).map((col, i) => (
        <div key={i}>
          <SectionH>{col.h}</SectionH>
          <div className="space-y-0.5">
            {Object.entries(col.body).map(([k, v]) => (
              <div key={k} className="text-[12px]"><span className="text-slate-500">{prettifyKey(k)}: </span><span className="text-slate-900">{String(v)}</span></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TableSection({ title, rows }) {
  return (
    <div>
      <SectionH>{title}</SectionH>
      <table className="w-full text-[12px]">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-t border-slate-100">
              <td className="py-1.5 px-2 text-slate-500 w-1/3">{k}</td>
              <td className="py-1.5 px-2 text-slate-900 font-semibold">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function prettifyKey(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ==================== Signature + QR panel ====================
// Renders a hand-drawn wet-ink signature (inline SVG path) + a certification
// stamp + a QR code linking to the doc's verification hash. Makes printed
// Blancco wipe certificates and ITAD grading reports feel courtroom-defensible.
function SignaturePanel({ doc, brandBlue, brandSoft }) {
  const signer =
    doc.operator
      ? { name: doc.operator.name, id: doc.operator.id, role: "Certified Blancco operator" }
      : { name: "M. Petersen", id: "ITAD-A0471", role: "ITAD Senior Grader" };

  const verifyHash = doc.hash_sha256 || `${doc.number}-${(doc.date || "").replace(/-/g, "")}`;
  const qrPayload = `flexspace://verify/${doc.id}/${doc.number}#${verifyHash.slice(0, 24)}`;
  const qrSrc = `https://quickchart.io/qr?text=${encodeURIComponent(qrPayload)}&size=140&margin=1&format=svg`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 pt-5 mt-3 border-t border-slate-200">
      {/* Signature block */}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Signed and certified</div>
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            {/* Hand-drawn signature — inline SVG path (looks like ink) */}
            <svg viewBox="0 0 240 70" className="w-52 h-16" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6,50 C 22,20 34,58 46,32 C 58,10 70,46 84,28 C 96,14 102,50 118,30 C 132,14 146,54 160,30 C 172,12 190,48 210,26 C 220,16 228,30 234,22"
                fill="none" stroke="#0F172A" strokeWidth="1.8" strokeLinecap="round"
                style={{ filter: "url(#inkBlur)" }}
              />
              <path
                d="M92,42 C 100,48 108,44 116,50"
                fill="none" stroke="#0F172A" strokeWidth="1.2" strokeLinecap="round"
              />
              <defs>
                <filter id="inkBlur"><feGaussianBlur stdDeviation="0.25" /></filter>
              </defs>
            </svg>
            <div className="border-t border-slate-400 mt-1 pt-1 w-52">
              <div className="text-[11px] font-bold text-slate-900">{signer.name}</div>
              <div className="text-[10px] text-slate-500">{signer.role} · ID {signer.id}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Signed {doc.date} · {doc.system}</div>
            </div>
          </div>
          {/* Certification stamp */}
          <div className="relative" aria-hidden>
            <svg viewBox="0 0 120 120" className="w-24 h-24">
              <circle cx="60" cy="60" r="52" fill="none" stroke={brandBlue} strokeWidth="2" opacity="0.6" />
              <circle cx="60" cy="60" r="44" fill="none" stroke={brandBlue} strokeWidth="1"  opacity="0.6" strokeDasharray="3 2" />
              <text x="60" y="46" textAnchor="middle" fontSize="8"  fill={brandBlue} fontWeight="700" letterSpacing="1">CERTIFIED</text>
              <text x="60" y="60" textAnchor="middle" fontSize="16" fill={brandBlue} fontWeight="800">VERIFIED</text>
              <text x="60" y="74" textAnchor="middle" fontSize="7"  fill={brandBlue} fontWeight="700" letterSpacing="1">{doc.system}</text>
              <text x="60" y="88" textAnchor="middle" fontSize="6.5" fill={brandBlue} opacity="0.7">{doc.date}</text>
            </svg>
          </div>
        </div>
      </div>

      {/* QR code + verify URL */}
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Scan to verify</div>
        <div className="inline-block rounded-md p-1.5" style={{ backgroundColor: "#fff", border: `1px solid ${brandSoft}` }}>
          <img src={qrSrc} alt={`Verification QR for ${doc.number}`} className="w-[110px] h-[110px]" />
        </div>
        <div className="text-[9px] font-mono text-slate-500 mt-2 max-w-[160px] break-all">{qrPayload}</div>
      </div>
    </div>
  );
}

